import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GitCompareArrows, Wand2 } from 'lucide-react';
import type { DriftFinding, DriftReconcilable, DriftSummary, Page } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { DriftFindingDialog } from './DriftFindingDialog';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

const STATUS_TONES: Record<DriftFinding['status'], 'warn' | 'success' | 'danger' | 'neutral'> = {
  open: 'warn',
  fixed: 'success',
  fix_failed: 'danger',
  dismissed: 'neutral',
};

/**
 * Config drift, reviewed fleet-first.
 *
 * The nightly check compares each chassis's running config against what
 * SimpleACS holds for it and files a finding per difference. This page is the
 * other half of that loop: which OLTs are drifting and for how long, what
 * exactly each difference is (both sides, the platform's and the chassis's),
 * and the two ways to close one — apply the fix the check recorded, or
 * dismiss it with a reason that stays on the row. Nothing is ever fixed
 * automatically; this page is where a person decides.
 */
export function DriftPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const [oltId, setOltId] = useState('');
  const [status, setStatus] = useState('open');
  const [openFindingId, setOpenFindingId] = useState<string | null>(null);
  /**
   * "Reconcile by resync" gathers the chassis's drifted ONUs and hands them to
   * a bulk `onu_resync` review — the one safe re-apply, at fleet scale, the way
   * the board already sends a single ZTE finding to the ONU page's Resync. It
   * navigates to the bulk screen rather than firing a run, so the operator sees
   * the count and confirms a fleet write deliberately. The findings clear when
   * the next nightly check confirms the chassis now matches.
   */
  const [reconciling, setReconciling] = useState(false);
  const [reconcileNote, setReconcileNote] = useState<string | null>(null);

  async function reconcile(target: { oltId: string; oltName: string }) {
    setReconciling(true);
    setReconcileNote(null);
    try {
      const r = await api.get<DriftReconcilable>('/drift/reconcilable', { oltId: target.oltId });
      if (r.onuIds.length === 0) {
        setReconcileNote(`Nothing on ${target.oltName} can be resynced — open findings name no ONU still on record.`);
        return;
      }
      navigate('/bulk-actions', { state: { oltId: r.oltId, onuIds: r.onuIds, action: 'onu_resync' } });
    } catch (err) {
      setReconcileNote(err instanceof Error ? err.message : 'Could not gather the drifted ONUs.');
    } finally {
      setReconciling(false);
    }
  }

  const summary = useQuery({
    queryKey: ['drift', 'summary'],
    queryFn: () => api.get<DriftSummary>('/drift/summary'),
  });
  const findings = useQuery({
    queryKey: ['drift', 'findings', oltId, status],
    queryFn: () =>
      api.get<Page<DriftFinding>>('/drift/findings', {
        oltId: oltId || undefined,
        status,
      }),
  });
  const olts = summary.data?.olts ?? [];
  const totals = summary.data?.totals;
  const items = findings.data?.items ?? [];
  const filteredOlt = olts.find((o) => o.oltId === oltId) ?? null;

  return (
    <>
      <PageHeader
        title="Config drift"
        description="What the nightly check found different between what SimpleACS holds and what each chassis runs. Open a finding to see both sides — apply the recorded fix, or dismiss it with a reason."
      />

      <Card
        title={
          totals
            ? `Fleet (${totals.open} open — ${totals.fixable} with a recorded fix, ${totals.reviewOnly} review-only)`
            : 'Fleet'
        }
        className="mb-6"
      >
        {summary.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : summary.error ? (
          <div className="p-5">
            <ErrorNotice error={summary.error} />
          </div>
        ) : olts.length === 0 ? (
          <EmptyState
            icon={<GitCompareArrows className="size-8" />}
            title="No OLTs yet"
            description="Drift is a statement about a chassis, and there are none on record to make one about."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">OLT</th>
              <th className="th">Vendor</th>
              <th className="th">Open findings</th>
              <th className="th">Oldest</th>
              <th className="th">Newest</th>
            </THead>
            <TBody>
              {olts.map((olt) => (
                <TRow
                  key={olt.oltId}
                  // The row is a filter: clicking narrows the table below to
                  // this chassis, clicking again widens it back to the fleet.
                  onClick={() => setOltId((current) => (current === olt.oltId ? '' : olt.oltId))}
                  className={oltId === olt.oltId ? 'bg-surface-2/70' : undefined}
                >
                  <td className="td font-medium text-ink">
                    {olt.oltName}
                    {olt.oltNumber !== null && (
                      <span className="ml-1.5 text-xs text-faint">#{olt.oltNumber}</span>
                    )}
                  </td>
                  <td className="td text-muted uppercase">{olt.vendor}</td>
                  <td className="td">
                    {olt.open === 0 ? (
                      <Pill tone="success">in step</Pill>
                    ) : (
                      <span className="flex flex-wrap gap-1.5">
                        <Pill tone="warn">{olt.open} open</Pill>
                        {olt.fixable > 0 && <Pill tone="info">{olt.fixable} fix recorded</Pill>}
                        {olt.reviewOnly > 0 && <Pill tone="neutral">{olt.reviewOnly} review-only</Pill>}
                      </span>
                    )}
                  </td>
                  <td className="td text-muted">
                    {olt.oldestOpenAt ? formatRelative(olt.oldestOpenAt) : '—'}
                  </td>
                  <td className="td text-muted">
                    {olt.latestOpenAt ? formatRelative(olt.latestOpenAt) : '—'}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        title={
          filteredOlt ? `Findings on ${filteredOlt.oltName} (${items.length})` : `Findings (${items.length})`
        }
        action={
          <span className="flex items-center gap-2">
            {filteredOlt && filteredOlt.open > 0 && can('bulk.execute') && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Wand2 className="size-3.5" />}
                loading={reconciling}
                onClick={() => reconcile(filteredOlt)}
                title={`Resync the ${filteredOlt.open} drifted ONU${filteredOlt.open === 1 ? '' : 's'} on ${filteredOlt.oltName} in one bulk run`}
              >
                Reconcile by resync
              </Button>
            )}
            {filteredOlt && (
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setOltId('')}
              >
                Show the whole fleet
              </button>
            )}
            <select
              className="input w-36"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="open">Open</option>
              <option value="fixed">Fixed</option>
              <option value="fix_failed">Fix failed</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">Any status</option>
            </select>
          </span>
        }
      >
        {reconcileNote && (
          <div className="border-b border-line bg-surface-2/60 px-5 py-2 text-xs text-muted">
            {reconcileNote}
          </div>
        )}
        {findings.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : findings.error ? (
          <div className="p-5">
            <ErrorNotice error={findings.error} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<GitCompareArrows className="size-8" />}
            title={status === 'open' ? 'Nothing has drifted' : 'No findings'}
            description={
              status === 'open'
                ? 'Every chassis in view matches what SimpleACS holds for it — that is the good outcome.'
                : 'No findings with this status.'
            }
          />
        ) : (
          <Table>
            <THead>
              <th className="th">ONU</th>
              <th className="th">OLT</th>
              <th className="th">What drifted</th>
              <th className="th">Fix</th>
              <th className="th">Status</th>
              <th className="th">Found</th>
            </THead>
            <TBody>
              {items.map((finding) => (
                <TRow key={finding.id} onClick={() => setOpenFindingId(finding.id)}>
                  <td className="td font-mono text-xs">{finding.subject}</td>
                  <td className="td text-muted">{finding.oltName ?? '—'}</td>
                  <td className="td text-muted">{finding.summary}</td>
                  <td className="td">
                    {finding.fixable ? (
                      <Pill tone="info">fix recorded</Pill>
                    ) : (
                      <Pill tone="neutral">review-only</Pill>
                    )}
                  </td>
                  <td className="td">
                    <Pill state tone={STATUS_TONES[finding.status]}>
                      {finding.status.replace('_', ' ')}
                    </Pill>
                  </td>
                  <td className="td text-muted">{formatRelative(finding.createdAt)}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Apply and Dismiss both close the dialog and report through the
          tasks tray, so the page keeps no toast of its own. */}
      {openFindingId && (
        <DriftFindingDialog findingId={openFindingId} onClose={() => setOpenFindingId(null)} />
      )}
    </>
  );
}
